import { Component, input, Input, OnInit, signal, TemplateRef } from '@angular/core';
import {
  PoButtonModule,
  PoFieldModule,
  PoSearchLiterals,
  PoSearchModule,
  PoSelectOption,
  PoTableColumn,
  PoTableColumnSort,
  PoTableModule,
} from '@po-ui/ng-components';
import { documentFormatter } from '../utils/formatterDocument';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo, gql, QueryRef } from 'apollo-angular';
import { NgTemplateOutlet } from '@angular/common';
import { formatDateToTimestampz } from '../utils/formatDateToTimestampz';
import { subDays } from 'date-fns';

interface FilterDTO {
  property: string;
  options: PoSelectOption[];
}
type PeriodValues = '30' | '60' | '90';

interface RequestInterface {
  whereTypeName: string;
  requestName: string;
  requestBody: string;
  requestWhere: Record<string, any>;
}
@Component({
  selector: 'app-dynamic-table',
  standalone: true,
  imports: [
    PoTableModule,
    PoSearchModule,
    PoFieldModule,
    PoButtonModule,
    NgTemplateOutlet,
  ],
  templateUrl: './dynamic-table.component.html',
  styleUrl: './dynamic-table.component.scss',
})
export class DynamicTableComponent implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private readonly apollo: Apollo
  ) { }

  tableQuery: QueryRef<any> = {} as QueryRef<any>;

  ngOnInit() {
    this.fetchData();
  }

  @Input() isURLState = true;
  @Input() request: RequestInterface | undefined;
  @Input() actions: TemplateRef<unknown> | null = null;

  page = 1;
  pageSize = 10;

  loading = true;
  loadingShowMore = false;
  showMoreDisabled = false;

  fetchData() {
    if (!this.request || !this.request.requestName) return;
    if (this.request) {
      this.tableQuery = this.apollo.watchQuery<
        Record<string, { items: any[] }>
      >({
        query: gql`
        query TableRequest($pagination: PaginationArgs, $where: ${this.request.whereTypeName}){
          ${this.request.requestBody}
          }
          `,

        variables: {
          pagination: {
            page: this.page,
            limit: this.pageSize,
            orderBy: this.order?.by,
            orderDirection: this.order?.direction.toUpperCase()
          },
          where: {
            ...this.request.requestWhere,
            [this.filter?.property || '']: this.filterValue,
            term:
              this.search && this?.search.trim()?.length > 0
                ? this.search?.trim()
                : undefined,
            endDate: this.periodFilterValue
              ? formatDateToTimestampz(
                subDays(new Date(), Number(this.periodFilterValue))
              )
              : undefined,
            startDate: this.periodFilterValue
              ? formatDateToTimestampz(new Date())
              : undefined,
          },
        },
      });

      this.tableQuery.valueChanges.subscribe((result: any) => {
        if (!this.request) return;
        const resultData = result.data[this?.request.requestName]
        const items = resultData.items || []

        this.page === 1
          ? (this.tableData.set(items))
          : (this.tableData.update(old => old.concat(items)));

        if (this.page === 1) {
          this.loading = result.loading;
        } else {
          this.loadingShowMore = result.loading;
        }
        if (items.length < this.pageSize || resultData.totalPages === this.page) {
          this.showMoreDisabled = true
        }
      });
    }
  }


  handleShowMore() {
    this.loadingShowMore = true;
    this.page++;

    if (this.isURLState) {
      if (this.isURLState && this.filter) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            page: this.page,
          },
          queryParamsHandling: 'merge',
        });
      }
    }
    this.fetchData();
  }

  // search
  search: string | undefined = undefined;
  searchLiterals: PoSearchLiterals = {
    search: 'Buscar por CNPJ, Nome ou TOTVS Code',
  };

  handleSearch(event: Event) {
    this.search = String(event);
    if (this.isURLState) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { search: String(event).length > 0 ? event : undefined },
        queryParamsHandling: 'merge',
      });
    }
    this.fetchData();
  }

  // Custom Filters
  @Input() filter?: FilterDTO;
  filterValue?: string = undefined;

  handleFilter(event: Event) {
    this.filterValue = String(event);
    this.page = 1;
    if (this.isURLState && this.filter) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          [this.filter.property]:
            String(event)?.length > 0 ? String(event) : undefined,
          page: 1,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  // Period Filters
  @Input() filterByPeriod = false;
  periodFilterValue: PeriodValues | undefined = undefined;
  periodFilters: PoSelectOption[] = [
    {
      label: 'Últimos 30 dias',
      value: '30',
    },
    {
      label: 'Últimos 60 dias',
      value: '60',
    },
    { label: 'Últimos 90 dias', value: '90' },
  ];

  handlePeriodFilter(event: Event) {
    this.periodFilterValue = String(event) as PeriodValues;
    this.page = 1;
    if (this.isURLState) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          period: String(event)?.length > 0 ? String(event) : undefined,
          page: 1,
        },
        queryParamsHandling: 'merge',
      });
    }
    this.fetchData();
  }

  //table
  @Input() tableColumns: PoTableColumn[] = [];

  tableData = signal<any[]>([]);
  order: { by: string; direction: string } | null = null;

  handleSort(sort: PoTableColumnSort) {
    const direction =
      sort.type[0] === 'a' ? sort.type.slice(0, 3) : sort.type.slice(0, 4);
    this.order = {
      by: sort?.column?.property || '',
      direction,
    };

    if (this.isURLState) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          orderBy: this?.order?.by,
          orderDirection: direction,
          page: 1,
        },
        queryParamsHandling: 'merge',
      });
    }
    this.fetchData();
  }
}
